import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useSiteConfig } from "@/lib/useSiteConfig";
import { CountUp } from "@/components/ImpactCounter";
import {
  ArrowRight,
  Ruler,
  MapPin,
  ShieldCheck,
  Sparkles,
  Recycle,
  Timer,
  PoundSterling,
  CheckCircle2,
  X,
  Flame,
  Star,
  Home,
  Store,
  Truck,
  Wrench,
  PackageCheck,
  Info,
} from "lucide-react";

const SERVICE_OPTIONS = [
  {
    icon: Store,
    title: "Collect yourself",
    body: "Pick up your flooring from the retailer.",
    key: "collect",
  },
  {
    icon: Truck,
    title: "Collection & delivery",
    body: "Have your flooring collected from the retailer and brought to your home.",
    key: "delivery",
  },
  {
    icon: Wrench,
    title: "Fitting only",
    body: "Arrange a fitter for flooring you've collected.",
    key: "fitting",
  },
  {
    icon: PackageCheck,
    title: "The complete service",
    body: "Have your flooring collected, delivered and fitted.",
    key: "complete",
  },
];
import PublicHeader from "@/components/PublicHeader";
import SearchForm from "@/components/SearchForm";
import CategoryGrid from "@/components/CategoryGrid";
import Seo, { organizationSchema, localBusinessSchema, faqSchema } from "@/seo/Seo";
import { GM_BOROUGHS, CATEGORIES, SITE_ORIGIN } from "@/seo/data";
import { AdminSignInLink } from "@/components/AdminSignInLink";

const LANDING_FAQS = [
  {
    q: "What is Floor-Match?",
    a: "Floor-Match is Greater Manchester's marketplace for surplus and remnant flooring. Independent flooring retailers list roll ends, remnants and pack surplus; consumers enter their room size and reserve the exact stock that fits, then collect from the retailer's local shop.",
  },
  {
    q: "How much cheaper is surplus flooring on Floor-Match?",
    a: "Prices are set by the retailer and vary by product, but surplus and remnant stock is typically 30–70 % below high-street retail because the retailer is clearing space rather than selling from an active range.",
  },
  {
    q: "Which areas does Floor-Match cover?",
    a: "All 10 Greater Manchester boroughs — Manchester, Salford, Stockport, Trafford, Tameside, Oldham, Rochdale, Bury, Bolton and Wigan. Search by postcode or borough to see live stock nearby.",
  },
  {
    q: "How do you match a remnant to my room?",
    a: "For rolled products (carpet, vinyl) we test both rotations — a 400 cm × 300 cm room fits a 400 cm × 350 cm roll rotated 90°. For pack products (laminate, LVT, carpet tiles) we calculate the room area, add a 10 % wastage allowance and only show listings with enough whole packs to cover the room.",
  },
];

const SAVINGS_EXAMPLES = [
  {
    room: "Bedroom",
    size: "12 m² of carpet",
    highStreet: "£240",
    floorMatch: "£99",
    saved: "£141",
    pct: "nearly 60%",
    type: "Carpet remnant",
    note: "Illustrative example at £20/m² original retail — not an actual listing or a measured customer average. Carpet only; fitting and underlay extra. Allow a little extra carpet for trimming, doorways and pattern matching.",
  },
  {
    room: "Living room",
    size: "400 cm × 500 cm",
    highStreet: "£980",
    floorMatch: "£310",
    saved: "£670",
    type: "LVT pack surplus",
  },
  {
    room: "Kitchen / diner",
    size: "300 cm × 400 cm",
    highStreet: "£560",
    floorMatch: "£185",
    saved: "£375",
    type: "Sheet vinyl roll end",
  },
];

const COMPARISON = [
  { row: "Price per m² on quality flooring", floormatch: "30–70 % below retail", bigbox: "Full retail price" },
  { row: "Availability", floormatch: "Walk in & collect locally", bigbox: "Order & wait 5–10 days" },
  { row: "Delivery fee", floormatch: "£0 — collect from local shop", bigbox: "£40–£90" },
  { row: "Wastage", floormatch: "Matched to your exact room", bigbox: "You over-buy 15–25 %" },
  { row: "Environmental impact", floormatch: "Rescues stock from landfill", bigbox: "New rolls, more waste" },
];

export default function Landing() {
  const [impact, setImpact] = useState(null);
  const { launch_phase } = useSiteConfig();
  const prelaunch = launch_phase !== "live";
  useEffect(() => {
    api.get("/impact/stats").then((r) => setImpact(r.data)).catch(() => {});
  }, []);
  const structured = [
    organizationSchema(),
    localBusinessSchema(),
    faqSchema(LANDING_FAQS),
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      url: SITE_ORIGIN,
      name: "Floor-Match",
      potentialAction: {
        "@type": "SearchAction",
        target: `${SITE_ORIGIN}/search?search_term_string={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    },
  ];
  return (
    <>
      <Seo
        title="Surplus flooring in Greater Manchester — save up to 70% on carpet, laminate, LVT & vinyl"
        description="Reserve surplus flooring from verified independent retailers across all 10 Greater Manchester boroughs. Carpet remnants, laminate pack ends, LVT surplus and sheet vinyl — matched to your room size, collected locally, 30–70 % below retail."
        path="/"
        keywords="carpet remnants Manchester, laminate flooring Greater Manchester, cheap flooring Stockport, LVT surplus Salford, vinyl remnants Bolton, flooring near me Manchester, flooring roll ends UK"
        structured_data={structured}
      />
      <PublicHeader />
      <main className="min-h-screen fm-grain pb-24 sm:pb-0" data-testid="landing-page">
        {/* Announcement bar — pre-launch vs customer-live */}
        <div className="bg-[#2A5A3B] text-white text-xs sm:text-sm" data-testid="announcement-bar">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2 flex items-center justify-center gap-2 flex-wrap">
            {prelaunch ? (
              <>
                <Store className="w-3.5 h-3.5 text-[#ffb078]" aria-hidden="true" />
                <span className="font-semibold">Retailer registration open — customer launch coming soon</span>
                <span className="hidden sm:inline text-white/70">·</span>
                <span className="hidden sm:inline">Founding retailers are building their catalogue now</span>
              </>
            ) : (
              <>
                <Flame className="w-3.5 h-3.5 text-[#ffb078]" aria-hidden="true" />
                <span className="font-semibold">Fresh remnants added every day</span>
                <span className="hidden sm:inline text-white/70">·</span>
                <span className="hidden sm:inline">Free to browse · No account needed · Collect today</span>
              </>
            )}
          </div>
        </div>

        {/* Hero — BOLDER PALETTE: dark green surface with orange accents */}
        <section className="relative overflow-hidden bg-[#0F3D2E]" data-testid="hero-bold">
          {/* Decorative texture layers */}
          <div
            className="absolute inset-0 opacity-40"
            style={{
              backgroundImage:
                "radial-gradient(circle at 15% 20%, rgba(224,89,21,0.28), transparent 45%), radial-gradient(circle at 85% 80%, rgba(42,90,59,0.6), transparent 40%)",
            }}
            aria-hidden="true"
          />
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)",
              backgroundSize: "44px 44px",
            }}
            aria-hidden="true"
          />
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20 pb-14 sm:pb-20">
            <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              <div className="lg:col-span-7 space-y-6">
                <span
                  className={`${prelaunch ? "hidden sm:inline-flex" : "inline-flex"} items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#ffb078] bg-[#E05915]/20 border border-[#E05915]/30 px-3 py-1.5 rounded-full`}
                  data-testid="hero-eyebrow"
                >
                  <Sparkles className="w-3.5 h-3.5" aria-hidden="true" /> {prelaunch ? "Retailer registration open — customer launch coming soon" : "Now live across Greater Manchester"}
                </span>
                <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight" data-testid="hero-headline">
                  Flooring deals you didn't know existed.
                </h1>
                <p className="font-display font-semibold text-xl sm:text-2xl text-[#ffb078]" data-testid="hero-supporting">
                  Premium flooring. Clearance prices.
                </p>
                <p className="text-lg sm:text-xl text-white/85 max-w-xl" data-testid="hero-description">
                  Save 30–70% on discounted remnants, roll ends and surplus flooring from retailers near you.
                </p>
                <p className="text-sm font-semibold text-[#9fe0b6] tracking-wide" data-testid="hero-tagline">
                  The perfect fit. Near you.
                </p>

                <div className="flex flex-wrap gap-2">
                  <TrustChipDark>
                    <PoundSterling className="w-3.5 h-3.5" /> 30–70% below retail
                  </TrustChipDark>
                  <TrustChipDark>
                    <Timer className="w-3.5 h-3.5" /> Reserve in 60 seconds
                  </TrustChipDark>
                  {prelaunch ? (
                    <TrustChipDark>
                      <Store className="w-3.5 h-3.5" /> Founding retailers signing up
                    </TrustChipDark>
                  ) : (
                    <TrustChipDark>
                      <MapPin className="w-3.5 h-3.5" /> Collect today
                    </TrustChipDark>
                  )}
                  <TrustChipDark>
                    <ShieldCheck className="w-3.5 h-3.5" /> Verified retailers only
                  </TrustChipDark>
                </div>

                <div className="flex flex-wrap gap-3 pt-2">
                  <a
                    href="#find-flooring"
                    className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#E05915] hover:bg-[#c24b10] text-white font-semibold text-base shadow-lg shadow-[#E05915]/30 transition-all"
                    data-testid="hero-cta-find"
                  >
                    Find my flooring <ArrowRight className="w-4 h-4" />
                  </a>
                  <Link
                    to="/search"
                    className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-white/10 hover:bg-white/15 border border-white/20 text-white font-semibold text-base backdrop-blur-sm transition-all"
                    data-testid="hero-cta-browse"
                  >
                    Browse deals
                  </Link>
                </div>

                <p className="flex items-center gap-2 text-sm text-white/75" data-testid="hero-service-line">
                  <Truck className="w-4 h-4 text-[#9fe0b6] shrink-0" aria-hidden="true" />
                  Collect yourself, arrange delivery, or have it delivered and fitted.
                </p>

                <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm" data-testid="hero-room-choice">
                  <span className="text-white/60">Flooring more than one room?</span>
                  <Link to="/whole-home" className="inline-flex items-center gap-1.5 text-[#9fe0b6] font-semibold hover:text-white transition-colors" data-testid="hero-whole-home">
                    Match flooring for several rooms <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/whole-home?demo=1" className="inline-flex items-center gap-1.5 text-white/70 underline hover:text-white transition-colors" data-testid="hero-whole-home-demo">
                    See a whole-home example
                  </Link>
                </div>
              </div>
              <div className="lg:col-span-5">
                <div className="relative">
                  <div className="absolute -inset-4 bg-gradient-to-br from-[#E05915]/30 to-[#2A5A3B]/40 rounded-3xl blur-2xl" aria-hidden="true" />
                  <img
                    src="https://customer-assets-lqy194kg.emergentagent.net/job_floormatch-demo/artifacts/pk3i8fa7_file_00000000b77081f4affe663f0bdb30a9.png"
                    alt="Warehouse stack of surplus carpet rolls in a range of colours, textures and patterns"
                    className="relative rounded-2xl w-full aspect-[4/5] object-cover shadow-2xl ring-1 ring-white/10"
                  />
                  <div className="absolute top-3 right-3 sm:-top-3 sm:-right-3 bg-[#E05915] text-white rounded-2xl px-4 py-3 shadow-lg rotate-3 ring-2 ring-white/20" data-testid="hero-saving-badge">
                    <div className="text-[10px] uppercase tracking-wider font-semibold opacity-90">Example saving</div>
                    <div className="font-display font-bold text-2xl leading-none mt-0.5">£141</div>
                    <div className="text-[10px] mt-1 opacity-90">on a bedroom carpet</div>
                  </div>
                  <div className="absolute bottom-3 left-3 sm:-bottom-4 sm:-left-4 bg-white border border-slate-200 rounded-xl p-3 shadow-md flex items-center gap-3">
                    <span className="grid place-items-center w-9 h-9 rounded-lg bg-[#2A5A3B] text-white">
                      <Recycle className="w-4 h-4" aria-hidden="true" />
                    </span>
                    <div className="text-xs">
                      <div className="font-semibold text-slate-900">Rescued from landfill</div>
                      <div className="text-slate-500">Every reservation, less waste</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Search Form Section */}
        <section id="find-flooring" className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 pt-10 sm:pt-14 scroll-mt-24">
          <div className="max-w-2xl mb-6 sm:mb-8" data-testid="search-intro">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#E05915]">
              Let's find your match
            </p>
            <h2 className="font-display font-bold text-2xl sm:text-3xl text-slate-900 mt-2 leading-tight">
              A room-sized search. A rather good saving.
            </h2>
            <p className="text-sm sm:text-base text-slate-600 mt-3 leading-relaxed">
              No endless scrolling through rolls that won't fit.
              <br className="hidden sm:block" />
              {" "}Tell us what you need. We'll do the measuring.
            </p>
          </div>
          <div className="mb-2 flex items-center justify-between gap-2 text-xs text-slate-500">
            <span className="font-semibold uppercase tracking-wider text-slate-700">
              Good things come in just-right sizes
            </span>
            <span className="hidden sm:inline">
              Typical bedroom: 300cm × 400cm
            </span>
          </div>
          {prelaunch && (
            <div className="mb-3 rounded-xl border border-[#E05915]/25 bg-[#E05915]/[0.06] px-4 py-3 text-sm text-slate-700 flex items-start gap-2.5" data-testid="prelaunch-search-note">
              <Store className="w-4 h-4 text-[#E05915] shrink-0 mt-0.5" aria-hidden="true" />
              <span>
                <span className="font-semibold text-slate-900">Try the matcher now.</span>{" "}
                We're still signing up founding retailers, so live stock is limited and online
                reservations open at launch. Examples shown are illustrative.
              </span>
            </div>
          )}
          <SearchForm />
          <p className="text-xs text-slate-500 mt-3">
            Try{" "}
            <span className="font-mono text-slate-700">M4 1HW</span> ·{" "}
            <span className="font-mono text-slate-700">M6 5FQ</span> ·{" "}
            <span className="font-mono text-slate-700">SK1 3AZ</span>
          </p>

          {/* Whole-home entry point — right beside the single-room search */}
          <Link
            to="/whole-home"
            className="group mt-4 flex items-center gap-3 rounded-2xl border border-[#0F3D2E]/15 bg-[#0F3D2E]/[0.04] hover:bg-[#0F3D2E]/[0.07] transition-colors px-4 py-3.5"
            data-testid="home-whole-home-cta"
          >
            <span className="grid place-items-center w-10 h-10 rounded-xl bg-[#0F3D2E] text-white shrink-0">
              <Home className="w-5 h-5" aria-hidden="true" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block font-display font-semibold text-slate-900">Flooring a whole home?</span>
              <span className="block text-sm text-slate-600">Plan several rooms at once and match them in one go.</span>
            </span>
            <ArrowRight className="w-5 h-5 text-[#0F3D2E] shrink-0 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
          </Link>
        </section>

        {/* Why these deals exist */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20" data-testid="why-deals">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            <div className="lg:col-span-6 space-y-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#E05915]">
                Why these deals exist
              </p>
              <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 leading-tight">
                Great flooring. Waiting for the right room.
              </h2>
              <p className="text-base text-slate-600 leading-relaxed max-w-xl">
                Flooring shops often have remnants, roll ends and surplus packs left over. The
                challenge is finding someone who needs that particular size, style or quantity.
              </p>
            </div>
            <div className="lg:col-span-6 space-y-3">
              <h3 className="font-display font-semibold text-xl text-slate-900">
                Why the smaller price tag?
              </h3>
              <p className="text-base text-slate-600 leading-relaxed">
                Advertising every piece individually takes time and money, while keeping it takes
                up valuable space. Disposal can cost money too.
              </p>
              <p className="text-base text-slate-600 leading-relaxed">
                FloorMatch brings these deals together and helps you find flooring that suits your
                room. Retailers clear space, you get a worthwhile saving, and more great flooring
                gets used instead of wasted.
              </p>
            </div>
          </div>
          <div
            className="mt-8 rounded-2xl bg-[#0F3D2E] text-white px-6 py-6 sm:px-8 sm:py-7 flex items-start gap-3"
            data-testid="why-deals-highlight"
          >
            <Recycle className="w-6 h-6 text-[#9fe0b6] shrink-0 mt-0.5" aria-hidden="true" />
            <ul className="font-display font-semibold text-lg sm:text-xl leading-snug space-y-1.5">
              <li>Retailers clear surplus stock.</li>
              <li>You find flooring that suits your room.</li>
              <li>More great flooring gets used instead of wasted.</li>
            </ul>
          </div>
        </section>

        {/* From shop to home — collection, delivery & fitting choices */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20" data-testid="shop-to-home">
          <div className="mb-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#E05915]">
              Collection, delivery &amp; fitting
            </p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 mt-1">
              From shop to home. Your choice.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {SERVICE_OPTIONS.map((o) => {
              const Icon = o.icon;
              return (
                <div
                  key={o.key}
                  className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-[#2A5A3B]/40 hover:shadow-sm transition"
                  data-testid={`service-option-${o.key}`}
                >
                  <span className="grid place-items-center w-10 h-10 rounded-lg bg-[#2A5A3B]/10 text-[#2A5A3B]">
                    <Icon className="w-5 h-5" aria-hidden="true" />
                  </span>
                  <h3 className="font-display font-semibold text-lg text-slate-900 mt-3">
                    {o.title}
                  </h3>
                  <p className="text-sm text-slate-600 mt-1">{o.body}</p>
                </div>
              );
            })}
          </div>
          <p
            className="text-sm text-slate-500 mt-4 flex items-start gap-2"
            data-testid="shop-to-home-note"
          >
            <Info className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
            Delivery and fitting cost extra and are subject to availability.
          </p>
        </section>

        {/* Savings strip — the big salesy hero-to-body bridge */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20" data-testid="savings-strip">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-6">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-[#E05915]">
                Illustrative savings examples
              </p>
              <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 mt-1">
                How much you could keep in your pocket
              </h2>
              <p className="text-slate-600 text-sm mt-2 max-w-xl">
                Illustrative pricing examples comparing Floor-Match surplus stock with buying the
                same quality new on the high street. These are not actual listings or measured
                customer averages — your actual saving depends on the room and stock available.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {SAVINGS_EXAMPLES.map((s) => (
              <div
                key={s.room}
                className="bg-white border border-slate-200 rounded-2xl p-6 relative overflow-hidden"
                data-testid={`savings-card-${s.room.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <div className="absolute top-4 right-4 bg-[#2A5A3B]/10 text-[#2A5A3B] text-xs font-semibold px-2 py-1 rounded-full">
                  Save {s.saved}{s.pct ? ` (${s.pct})` : ""}
                </div>
                <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                  {s.type}
                </p>
                <h3 className="font-display font-semibold text-xl text-slate-900 mt-1">
                  {s.room}
                </h3>
                <p className="text-sm text-slate-500 mt-0.5">{s.size}</p>
                <div className="mt-5 space-y-1.5">
                  <div className="flex justify-between items-baseline text-sm">
                    <span className="text-slate-500">High-street price</span>
                    <span className="text-slate-500 line-through">{s.highStreet}</span>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="text-slate-700 font-semibold">On Floor-Match</span>
                    <span className="font-display font-bold text-2xl text-[#E05915]">
                      {s.floorMatch}
                    </span>
                  </div>
                </div>
                {s.note && (
                  <p className="text-[11px] text-slate-400 mt-3 leading-relaxed" data-testid={`savings-note-${s.room.replace(/\s+/g, "-").toLowerCase()}`}>
                    {s.note}
                  </p>
                )}
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-4 max-w-2xl" data-testid="savings-disclaimer">
            Illustrative examples only, not actual listings or measured customer averages. Prices
            are set by retailers and vary by product and stock. Carpet examples are for carpet only;
            fitting and underlay are extra.
          </p>
        </section>

        {/* Shop Surplus Flooring — category discovery */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20" data-testid="shop-categories">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-6">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-[#E05915]">
                Shop surplus flooring
              </p>
              <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 mt-1">
                Browse by flooring type
              </h2>
              <p className="text-slate-600 text-sm mt-2 max-w-xl">
                Roll ends, remnants, offcuts, surplus packs and clearance ranges from independent
                retailers near you — filtered to fit your room.
              </p>
            </div>
          </div>
          <CategoryGrid />
        </section>

        {/* How it works */}
        <section id="how-it-works" className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20 scroll-mt-28" data-testid="how-it-works">
          <div className="mb-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#E05915]">
              How it works
            </p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 mt-1">
              {prelaunch ? "Measure, match, reserve — all local." : "Measure, reserve, collect — often the same day."}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              {
                n: "1",
                t: "Measure once",
                d: "Grab a tape measure — the length and width of each room (in cm or inches) is all we need. Everything is filtered to actually fit.",
              },
              {
                n: "2",
                t: "Reserve online",
                d: "Pay online to hold the stock. Retailer name and shop address unlock in your confirmation email instantly.",
              },
              {
                n: "3",
                t: prelaunch ? "Collect locally" : "Collect today",
                d: prelaunch
                  ? "Once we launch, you'll walk into the local shop within opening hours, quote your reference code and take your flooring home."
                  : "Walk into the local shop within opening hours, quote your reference code, take your flooring home. No long wait.",
              },
            ].map((s) => (
              <div
                key={s.n}
                className="rounded-2xl border border-slate-200 bg-white p-6"
              >
                <span className="grid place-items-center w-9 h-9 rounded-lg bg-[#E05915]/10 text-[#E05915] font-display font-bold">
                  {s.n}
                </span>
                <h3 className="font-display font-semibold text-lg text-slate-900 mt-3">{s.t}</h3>
                <p className="text-sm text-slate-600 mt-1">{s.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Comparison — the salesy "we vs them" grid */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20" data-testid="comparison-strip">
          <div className="mb-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#E05915]">
              Floor-Match vs. the big-box store
            </p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 mt-1">
              Why Manchester is switching to surplus.
            </h2>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <div className="hidden sm:grid sm:grid-cols-3 border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500 font-semibold">
              <div className="px-5 py-3"></div>
              <div className="px-5 py-3 border-l border-slate-200 text-[#2A5A3B]">Floor-Match</div>
              <div className="px-5 py-3 border-l border-slate-200">Big-box retailer</div>
            </div>
            {COMPARISON.map((c, i) => (
              <div
                key={c.row}
                className={`grid sm:grid-cols-3 ${i === COMPARISON.length - 1 ? "" : "border-b border-slate-200"}`}
              >
                <div className="px-5 py-4 font-semibold text-slate-800 text-sm sm:text-base">
                  {c.row}
                </div>
                <div className="px-5 py-3 sm:border-l border-slate-200 flex items-center gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-[#2A5A3B] shrink-0" aria-hidden="true" />
                  <span className="text-slate-800 font-medium">{c.floormatch}</span>
                </div>
                <div className="px-5 py-3 sm:border-l border-slate-200 flex items-center gap-2 text-sm">
                  <X className="w-4 h-4 text-slate-400 shrink-0" aria-hidden="true" />
                  <span className="text-slate-500">{c.bigbox}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Testimonials — launch-honest social proof */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20" data-testid="testimonials">
          <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-[#E05915]">
                Illustrative examples
              </p>
              <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 mt-1">
                How FloorMatch helps shoppers.
              </h2>
              <p className="text-sm text-slate-500 mt-2 max-w-xl">
                The quotes below are illustrative examples of how the service works — not genuine customer endorsements.
              </p>
            </div>
            <div className="flex items-center gap-1 text-slate-700 text-sm">
              <div className="flex items-center gap-0.5 text-[#E05915]">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star key={n} className="w-4 h-4 fill-current" aria-hidden="true" />
                ))}
              </div>
              <span className="ml-1 font-semibold">Illustrative</span>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              {
                q: "Found the exact carpet remnant for our spare room — half the price of the DIY warehouse and picked it up the same afternoon.",
                who: "Sarah T.",
                where: "Chorlton, M21",
              },
              {
                q: "Reserved a 4m sheet vinyl end on Monday, collected Tuesday. Fitter had it down by Wednesday. Saved us over £300.",
                who: "James W.",
                where: "Stockport, SK1",
              },
              {
                q: "Genuinely surprised — a local shop I'd never heard of had a full pack of the LVT I'd been eyeing at Wickes for a third of the price.",
                who: "Priya K.",
                where: "Salford, M6",
              },
            ].map((t) => (
              <div
                key={t.who}
                className="bg-white border border-slate-200 rounded-2xl p-6 flex flex-col"
                data-testid={`testimonial-${t.who.split(" ")[0].toLowerCase()}`}
              >
                <div className="flex items-center gap-0.5 text-[#E05915] mb-3">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} className="w-3.5 h-3.5 fill-current" aria-hidden="true" />
                  ))}
                </div>
                <p className="text-slate-700 text-sm leading-relaxed flex-1">"{t.q}"</p>
                <div className="mt-4 pt-4 border-t border-slate-100 text-xs">
                  <div className="font-semibold text-slate-900">{t.who}</div>
                  <div className="text-slate-500">{t.where}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Green impact strip — links to the Sustainability page */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20" data-testid="green-strip">
          <div className="rounded-3xl bg-[#0F3D2E] text-white overflow-hidden relative">
            <div
              className="absolute inset-0 opacity-40"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 85% 20%, rgba(42,90,59,0.7), transparent 45%)",
              }}
              aria-hidden="true"
            />
            <div className="relative px-6 sm:px-10 py-10 sm:py-12 grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7">
                <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#9fe0b6] bg-[#2A5A3B]/40 border border-[#2A5A3B]/60 px-3 py-1.5 rounded-full">
                  <Recycle className="w-3.5 h-3.5" aria-hidden="true" /> Good for your wallet, good for the planet
                </span>
                <h2 className="font-display font-bold text-3xl sm:text-4xl text-white mt-4 leading-tight">
                  Every reservation keeps flooring out of landfill.
                </h2>
                <p className="text-white/85 text-sm sm:text-base mt-3 max-w-xl">
                  Around 88,700 tonnes of flooring is buried in UK landfills each year — and less
                  than 5% of carpet is ever recycled. Choosing surplus reuses flooring that already
                  exists, no new waste required.
                </p>
                {impact && impact.rooms_rescued > 0 && (
                  <div
                    className="mt-5 inline-flex flex-wrap items-center gap-x-6 gap-y-2 bg-white/10 border border-white/20 rounded-2xl px-5 py-3 backdrop-blur-sm"
                    data-testid="green-strip-live"
                  >
                    <span className="inline-flex items-baseline gap-1.5">
                      <span className="font-display font-bold text-2xl text-white">
                        <CountUp value={impact.rooms_rescued} />
                      </span>
                      <span className="text-xs text-white/80">rooms rescued so far</span>
                    </span>
                    <span className="inline-flex items-baseline gap-1.5">
                      <span className="font-display font-bold text-2xl text-[#9fe0b6]">
                        <CountUp value={impact.weight_kg} decimals={0} />
                        <span className="text-base"> kg</span>
                      </span>
                      <span className="text-xs text-white/80">kept out of landfill</span>
                    </span>
                  </div>
                )}
                <Link
                  to="/sustainability"
                  className="inline-flex items-center gap-2 mt-6 h-11 px-5 rounded-full bg-white text-[#0F3D2E] font-semibold text-sm hover:bg-[#eafff1] transition-colors"
                  data-testid="green-strip-cta"
                >
                  See our green impact <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </Link>
              </div>
              <div className="lg:col-span-5 grid grid-cols-3 gap-3">
                {[
                  { big: "88,700t", small: "buried in the UK yearly" },
                  { big: "< 5%", small: "of carpet recycled" },
                  { big: "1,000 yrs", small: "to break down" },
                ].map((s) => (
                  <div
                    key={s.small}
                    className="bg-white/10 border border-white/20 rounded-2xl p-4 text-center backdrop-blur-sm"
                  >
                    <div className="font-display font-bold text-xl sm:text-2xl text-[#9fe0b6] leading-none">
                      {s.big}
                    </div>
                    <div className="text-[11px] text-white/80 mt-1.5 leading-snug">{s.small}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Feature Bento */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="mb-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#E05915]">
              Built for Manchester rooms
            </p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 mt-1">
              Every listing, matched to your space.
            </h2>
          </div>
          <div className="grid md:grid-cols-12 gap-4">
            <Feature
              className="md:col-span-6 bg-white"
              icon={<Ruler className="w-5 h-5" />}
              title="Matched to your measurements"
              body="For carpet and vinyl we only show stock big enough for your exact room dimensions, with clear waste and offcut estimates before you reserve."
              testId="feature-fit"
            />
            <Feature
              className="md:col-span-6 bg-[#2A5A3B] text-white border-transparent"
              iconColor="bg-white text-[#2A5A3B]"
              icon={<MapPin className="w-5 h-5" />}
              title="Local independents"
              body="Every listing comes from a real independent flooring retailer in Greater Manchester. Collect within hours, not weeks."
              variant="dark"
              testId="feature-local"
            />
            <Feature
              className="md:col-span-4 bg-white"
              icon={<Timer className="w-5 h-5" />}
              title="Reserve in 60 seconds"
              body={prelaunch
                ? "At launch you'll reserve online in seconds and collect from the local shop. Reservations hold your stock for up to 48 hours as a safety net."
                : "Pay online, walk into the shop the same day. Reservations hold your stock for up to 48 hours as a safety net — most customers collect within a few hours."}
              testId="feature-reserve"
            />
            <Feature
              className="md:col-span-4 bg-white"
              icon={<ShieldCheck className="w-5 h-5" />}
              title="Honest condition ratings"
              body="Every remnant, roll end and surplus pack is marked new, like new or good, so you know exactly what you're getting."
              testId="feature-honest"
            />
            <Feature
              className="md:col-span-4 bg-[#E05915] text-white border-transparent"
              iconColor="bg-white text-[#E05915]"
              icon={<Sparkles className="w-5 h-5" />}
              title="Premium flooring, everyday price"
              body="The same stock that costs hundreds in a showroom becomes affordable when it happens to fit your room."
              variant="dark"
              testId="feature-quality"
            />
          </div>
        </section>

        {/* Retailer CTA */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16 sm:pb-24">
          <div className="bg-white border border-slate-200 rounded-2xl p-8 sm:p-12 grid md:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-[#E05915]">
                For carpet & flooring retailers
              </p>
              <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 mt-2">
                {prelaunch ? "Turn dead stock into cash — get set for launch." : "Turn dead stock into cash — this month."}
              </h2>
              <p className="text-slate-600 mt-4 max-w-md">
                {prelaunch ? (
                  <>Roll ends and pack surplus sitting in your warehouse are working against you.
                  List them on Floor-Match now as a founding retailer, so you're in front of local
                  buyers the moment we launch — and clear space for the next range.</>
                ) : (
                  <>Roll ends and pack surplus sitting in your warehouse are working against you.
                  List them on Floor-Match, reach local buyers actively searching for flooring that
                  fits, and clear space for the next range.</>
                )}
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/sell"
                  className="fm-btn-primary inline-flex items-center gap-2"
                  data-testid="retailer-sell-cta"
                >
                  Start selling — free to list <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </Link>
                <Link
                  to="/retailer/login"
                  className="inline-flex items-center gap-2 text-slate-700 font-semibold hover:text-[#E05915] transition-colors"
                  data-testid="retailer-signin-cta"
                >
                  I already have an account
                </Link>
              </div>
              <ul className="mt-6 grid grid-cols-2 gap-2 text-sm text-slate-700">
                <li className="flex items-start gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-[#2A5A3B] shrink-0 mt-0.5" /> No upfront fees
                </li>
                <li className="flex items-start gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-[#2A5A3B] shrink-0 mt-0.5" /> Only pay when you sell
                </li>
                <li className="flex items-start gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-[#2A5A3B] shrink-0 mt-0.5" /> Mark sold in one tap
                </li>
                <li className="flex items-start gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-[#2A5A3B] shrink-0 mt-0.5" /> Live sales dashboard
                </li>
              </ul>
            </div>
            <div>
              <img
                src="https://customer-assets-lqy194kg.emergentagent.net/job_floormatch-demo/artifacts/i8zw956m_Man%20Capturing%20Product.png"
                alt="A retailer kneeling on a warehouse floor photographing a rolled-out grey carpet remnant with a phone"
                loading="lazy"
                decoding="async"
                className="rounded-xl w-full aspect-[5/4] object-cover"
              />
            </div>
          </div>
        </section>

        {/* SEO internal-link hub — crawlers use this to discover the 65
            location and category landing pages. Also gives human visitors
            a fast way to jump to their borough. */}
        <section className="border-t border-slate-200 bg-slate-50" data-testid="landing-seo-hub">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
            <h2 className="font-display font-semibold text-2xl text-slate-900 mb-2">
              Surplus flooring by Greater Manchester borough
            </h2>
            <p className="text-slate-600 text-sm mb-6">
              Live stock from verified independent retailers across every corner of Greater Manchester.
            </p>
            <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {GM_BOROUGHS.map((b) => (
                <li key={b.slug}>
                  <Link
                    to={`/flooring/${b.slug}`}
                    className="block bg-white border border-slate-200 rounded-xl px-4 py-3 hover:border-emerald-900 hover:shadow-sm transition"
                    data-testid={`borough-hub-${b.slug}`}
                  >
                    <div className="font-medium text-slate-900">Flooring in {b.name}</div>
                    <div className="text-xs text-slate-500 mt-1">{b.postcodes.slice(0, 3).join(", ")}…</div>
                  </Link>
                </li>
              ))}
            </ul>
            <h2 className="font-display font-semibold text-2xl text-slate-900 mt-12 mb-2">
              Browse by flooring type
            </h2>
            <ul className="flex flex-wrap gap-2 mt-2">
              {CATEGORIES.flatMap((c) =>
                GM_BOROUGHS.slice(0, 5).map((b) => (
                  <li key={`${c.slug}-${b.slug}`}>
                    <Link
                      to={`/flooring/${b.slug}/${c.slug}`}
                      className="inline-block bg-white border border-slate-200 text-slate-700 hover:bg-slate-100 rounded-full px-3 py-1 text-sm"
                    >
                      {c.name} in {b.name}
                    </Link>
                  </li>
                )),
              )}
            </ul>
          </div>
        </section>

        <footer className="border-t border-slate-200 py-8">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-sm text-slate-500">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-slate-700 tracking-wide uppercase text-[11px]">
                FloorMatch
              </span>
              <span className="text-slate-400">·</span>
              <span className="text-[#2A5A3B] font-semibold">The perfect fit. Near you.</span>
            </div>
            <div className="flex items-center gap-4">
              <Link
                to="/sustainability"
                className="hover:text-[#2A5A3B] transition-colors"
                data-testid="footer-sustainability-link"
              >
                Sustainability
              </Link>
              <AdminSignInLink />
              <span>© 2026 FloorMatch — Made in Manchester</span>
            </div>
          </div>
        </footer>

        {/* Sticky mobile CTA — massive conversion helper for phones */}
        <div
          className="sm:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-slate-200 px-4 py-3 shadow-[0_-6px_18px_rgba(0,0,0,0.06)]"
          data-testid="mobile-sticky-cta"
        >
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                Save up to 70%
              </div>
              <div className="text-sm font-semibold text-slate-900 leading-tight">
                Find flooring that fits your room
              </div>
            </div>
            <Link
              to="/search"
              className="fm-btn-primary inline-flex items-center gap-1.5 text-sm px-4 py-2.5"
              data-testid="mobile-sticky-cta-btn"
            >
              Search <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </main>
    </>
  );
}

function TrustChipDark({ children }) {
  return (
    <span className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 text-white text-xs font-semibold px-3 py-1.5 rounded-full backdrop-blur-sm">
      {children}
    </span>
  );
}

function Feature({ icon, title, body, className, iconColor, variant, testId }) {
  const isDark = variant === "dark";
  return (
    <div
      className={`rounded-2xl border border-slate-200 p-6 sm:p-7 ${className}`}
      data-testid={testId}
    >
      <div
        className={`grid place-items-center w-10 h-10 rounded-lg ${
          iconColor || "bg-[#E05915]/10 text-[#E05915]"
        }`}
      >
        {icon}
      </div>
      <h3
        className={`font-display font-semibold text-xl mt-4 ${
          isDark ? "text-white" : "text-slate-900"
        }`}
      >
        {title}
      </h3>
      <p className={`mt-2 text-sm ${isDark ? "text-white/85" : "text-slate-600"}`}>{body}</p>
    </div>
  );
}
